import type {
  Puzzle,
  SolveResult,
  PuzzleClues,
  FullSettleResult,
  SolutionAnalysisOptions,
  SolutionAnalysisResult,
} from '../types.js';
import { CellValue } from '../types.js';
import {
  solveGrid,
  analyzeSolutionsGrid,
  fullSettle as fullSettleGrid,
} from './grid-solver.js';

/** Solve a nonogram puzzle, returning steps and grid */
export function solve(puzzle: Puzzle): SolveResult {
  return solveGrid(puzzle);
}

/** Count solutions with an optional search budget. */
export function analyzeSolutions(
  puzzle: Puzzle,
  options: SolutionAnalysisOptions = {},
): SolutionAnalysisResult {
  return analyzeSolutionsGrid(puzzle, options);
}

/** Check if a puzzle has exactly one solution */
export function isUnique(puzzle: Puzzle): boolean {
  const result = analyzeSolutions(puzzle, { limit: 2 });
  return result.complete && result.count === 1;
}

/** Count solutions up to a limit (default 2 — enough for uniqueness check) */
export function countSolutions(puzzle: Puzzle, limit = 2): number {
  return analyzeSolutions(puzzle, { limit }).count;
}

/**
 * FullSettle: keep alternating h-sweep and v-sweep until no more progress.
 * Pure line-logic only — no backtracking.
 *
 * Based on the FullSettle operation from Batenburg et al. (2009).
 */
export function fullSettle(
  grid: import('../types.js').CellValue[][],
  clues: PuzzleClues,
): FullSettleResult {
  return fullSettleGrid(grid, clues);
}

/**
 * Find the single best cell to toggle to move the puzzle toward uniqueness.
 *
 * Strategy: fullSettle the current clues to find undetermined cells.
 * For each undetermined cell, temporarily set it to the design's value,
 * recompute clues, re-settle, and measure how many cells become resolved.
 * Return the cell that resolves the most others (maximum cascade).
 *
 * This guarantees convergence — each suggestion strictly increases the
 * number of settled cells, so it can never cycle.
 */
export function findUniquenessHint(
  puzzle: Puzzle,
  designGrid: boolean[][],
  cluesFromGrid: (grid: boolean[][]) => PuzzleClues,
): { row: number; col: number; shouldFill: boolean } | null {
  const { rows, cols } = puzzle;

  // Settle current puzzle to find undetermined cells
  const emptyGrid: CellValue[][] = Array.from(
    { length: rows },
    () => Array(cols).fill(CellValue.Unknown),
  );
  const baseSettle = fullSettle(emptyGrid, puzzle.clues);
  if (baseSettle.remainingUnknowns === 0) return null; // already fully determined

  // Collect undetermined cell positions
  const undetermined: { row: number; col: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (baseSettle.grid[r][c] === CellValue.Unknown) {
        undetermined.push({ row: r, col: c });
      }
    }
  }

  // For each undetermined cell, try toggling it and measure cascade
  let bestCell: { row: number; col: number; shouldFill: boolean } | null = null;
  let bestResolved = 0;

  for (const cell of undetermined) {
    const shouldFill = !designGrid[cell.row][cell.col];
    const testGrid = designGrid.map(row => [...row]);
    testGrid[cell.row][cell.col] = shouldFill;

    const testClues = cluesFromGrid(testGrid);
    const testEmpty: CellValue[][] = Array.from(
      { length: rows },
      () => Array(cols).fill(CellValue.Unknown),
    );
    const testSettle = fullSettle(testEmpty, testClues);
    const resolved = baseSettle.remainingUnknowns - testSettle.remainingUnknowns;

    if (resolved > bestResolved) {
      bestResolved = resolved;
      bestCell = { row: cell.row, col: cell.col, shouldFill };
    }
  }

  // If no single toggle improves things, return first undetermined cell as fallback
  if (!bestCell && undetermined.length > 0) {
    const cell = undetermined[0];
    bestCell = { row: cell.row, col: cell.col, shouldFill: !designGrid[cell.row][cell.col] };
  }

  return bestCell;
}
